import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import * as LaunchDarkly from 'launchdarkly-js-client-sdk';
import { environment } from '../../../environments/environment';

const EARLY_DEPOSIT_CUTOFF = new Date('2026-10-07T05:00:00.000Z');

export interface FeatureFlags {
  laftFeatureEnabled: boolean;
  laftJuridicaFeatureEnabled: boolean;
  teaFeatureEnabled: boolean;
  laftClientFeatureEnabled: boolean;
  earlyAdvanceEnabled: boolean;
  costoSinIgvPlusOneEnabled: boolean;
  faproEnabled: boolean;
  earlyDepositEnabled: boolean;
  otdFundInvoiceSaleEnabled: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class FeatureFlagService {
  private ldClient: LaunchDarkly.LDClient | null = null;
  private flagsSubject = new BehaviorSubject<FeatureFlags>({
    laftFeatureEnabled: environment.LAFTFeature, // Fallback to env
    laftJuridicaFeatureEnabled: environment.LAFTJuridicaFeature,
    teaFeatureEnabled: environment.teaFeatureEnabled,
    laftClientFeatureEnabled: environment.laftClientFeatureEnabled,
    earlyAdvanceEnabled: environment.earlyAdvanceEnabled,
    costoSinIgvPlusOneEnabled: environment.costoSinIgvPlusOneEnabled,
    faproEnabled: environment.faproEnabled,
    earlyDepositEnabled: environment.earlyDepositEnabled,
    otdFundInvoiceSaleEnabled: environment.otdFundInvoiceSaleEnabled,
  });

  public flags$: Observable<FeatureFlags> = this.flagsSubject.asObservable();

  private flagsResolvedSubject = new BehaviorSubject<boolean>(false);
  public flagsResolved$: Observable<boolean> = this.flagsResolvedSubject.asObservable();

  private initialized = false;

  private userRegisteredAt: Date | null = null;

  constructor() {}

  private updateFlags(rawFlags: any): void {
    const flags: FeatureFlags = {
      laftFeatureEnabled: rawFlags['laft-feature-enabled'] ?? environment.LAFTFeature,
      laftJuridicaFeatureEnabled: rawFlags['laft-juridica-feature'] ?? environment.LAFTJuridicaFeature,
      teaFeatureEnabled: rawFlags['tea-feature-enabled'] ?? environment.teaFeatureEnabled,
      laftClientFeatureEnabled: rawFlags['laft-client-feature'] ?? environment.laftClientFeatureEnabled,
      earlyAdvanceEnabled: rawFlags['early-advance'] ?? environment.earlyAdvanceEnabled,
      costoSinIgvPlusOneEnabled: rawFlags['costo-sin-igv-plus-one'] ?? environment.costoSinIgvPlusOneEnabled,
      faproEnabled: rawFlags['fapro'] ?? environment.faproEnabled,
      earlyDepositEnabled: rawFlags['laft-pn-v2'] ?? environment.earlyDepositEnabled,
      otdFundInvoiceSaleEnabled: rawFlags['otd-fund-invoice-sale'] ?? environment.otdFundInvoiceSaleEnabled,
    };

    console.log('[LaunchDarkly] All Flags', flags);

    this.flagsSubject.next(flags);
  }

  getFlag(flagKey: keyof FeatureFlags): boolean {
    return this.flagsSubject.value[flagKey] ?? false;
  }

  isLAFTEnabled(): boolean {
    return this.getFlag('laftFeatureEnabled');
  }

  isLAFTJuridicaEnabled(): boolean {
    return this.getFlag('laftJuridicaFeatureEnabled');
  }

  isTeaEnabled(): boolean {
    return this.getFlag('teaFeatureEnabled');
  }

  isLAFTClientEnabled(): boolean {
    return this.getFlag('laftClientFeatureEnabled');
  }

  isEarlyAdvanceEnabled(): boolean {
    return this.getFlag('earlyAdvanceEnabled');
  }

  isCostoSinIgvPlusOneEnabled(): boolean {
    return this.getFlag('costoSinIgvPlusOneEnabled');
  }

  isFaproEnabled(): boolean {
    return this.getFlag('faproEnabled');
  }

  isOtdFundInvoiceSaleEnabled(): boolean {
    return this.getFlag('otdFundInvoiceSaleEnabled');
  }

  isEarlyDepositEnabled(): boolean {
    if (this.userRegisteredAt && this.userRegisteredAt < EARLY_DEPOSIT_CUTOFF) {
      return false;
    }

    return this.getFlag('earlyDepositEnabled');
  }

  identifyUser(user: any): void {
    const registeredAt = user?.createdAt ? new Date(user.createdAt) : null;
    this.userRegisteredAt = registeredAt && !isNaN(registeredAt.getTime()) ? registeredAt : null;

    if (!this.initialized) {
      this.initialized = true;

      let context = {};

      if (!user?.status) {
        context = {
          kind: 'user',
          key: user._id,
          anonymous: true,
        };
      } else {
        context = {
          kind: 'user',
          key: user?._id,
          email: user?.email,
          name: `${user?.names} ${user?.familyNames}`,
          status: user?.status,
          role: user?.role,
          idType: user?.idType,
          segment: user?.segment,
        };
      }

      this.ldClient = this.createClient(context);

      this.ldClient
        .waitForInitialization()
        .then(() => {
          console.log('[FeatureFlags] SDK initialized successfully');

          const allFlags = this.ldClient!.allFlags();
          this.updateFlags(allFlags);
          this.flagsResolvedSubject.next(true);

          this.ldClient!.on('change', () => {
            const updatedFlags = this.ldClient!.allFlags();
            this.updateFlags(updatedFlags);
          });

          return this.ldClient!.flush();
        })
        .catch(error => {
          console.error('[FeatureFlags] ❌ Failed to initialize:', error);
          this.flagsResolvedSubject.next(true);
        });
    } else {
      if (!this.ldClient) {
        console.warn('[FeatureFlags] Client not initialized yet, skipping identify');
        return;
      }

      let context = {};

      if (!user?.status) {
        context = {
          kind: 'user',
          key: user._id,
          anonymous: true,
        };
      } else {
        context = {
          kind: 'user',
          key: user?._id,
          email: user?.email,
          name: `${user?.names} ${user?.familyNames}`,
          status: user?.status,
          role: user?.role,
          idType: user?.idType,
          segment: user?.segment,
        };
      }

      this.ldClient
        .waitForInitialization()
        .then(() => {
          return this.ldClient!.identify(context);
        })
        .then(() => {
          return this.ldClient!.flush();
        })
        .catch(error => {
          console.error('[FeatureFlags] Failed to identify user:', error);
        });
    }
  }

  private createClient(context: LaunchDarkly.LDContext): LaunchDarkly.LDClient {
    return LaunchDarkly.initialize(environment.launchDarklyClientId, context, {
      bootstrap: 'localStorage',
      streaming: true,
      sendEvents: true,
      allAttributesPrivate: false,
      evaluationReasons: false,
      flushInterval: 30000,
    });
  }

  destroy(): void {
    if (this.ldClient) {
      this.ldClient
        .close()
        .then(() => {
          console.log('[FeatureFlags] Closed LaunchDarkly connection');
        })
        .catch(err => {
          console.error('[FeatureFlags] Error closing:', err);
        });
      this.ldClient = null;
      this.initialized = false;
      this.userRegisteredAt = null;
      this.flagsResolvedSubject.next(false);
    }
  }

  refresh(): void {
    if (this.ldClient) {
      this.ldClient
        .flush()
        .then(() => {
          console.log('[FeatureFlags] Flags refreshed');
        })
        .catch(err => {
          console.error('[FeatureFlags] Error refreshing:', err);
        });
    }
  }
}
